import React from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { useNavigate } from 'react-router-dom';
import { authLogin, sendVerificationEmail, resetPassword } from "../../store/actions/authentication";
import withRouter from "../../components/Common/withRouter";
import whitelog from "../../assets/images/transparent.png";
import { showWarningDialog } from "../../utils/dialogs";
import { login } from "../../utils/auth";
import Swal from 'sweetalert2';
import { FaMailBulk } from "react-icons/fa";
import { MdEmail, MdLock, MdOutlineEmail } from "react-icons/md";
import { FiMail, FiLock } from "react-icons/fi";
import {
  AppleCard,
  AppleCardHeader,
  AppleCardBody,
  AppleButton,
  AppleInput,
  AppleLabel,
  AppleForm,
  AppleFormGroup,
  AppleFormFeedback,
  ErrorMessage,
  AppleAlert
} from '../../components/AppleStyles';

const Login = (props) => {
    document.title = "Login | Meinhardt EPCM M-SPHERE";
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { control, handleSubmit, formState: { errors }, getValues } = useForm();
    const { error } = useSelector(state => state.login);

    const showEmailVerificationDialog = (message, data) => {
        Swal.fire({
            title: 'Email Verification Required',
            text: message,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Send Verification Link',
            cancelButtonText: 'OK'
        }).then((result) => {
            if (result.isConfirmed) {
                sendVerificationLink(data.email);
            }
        });
    };

    const sendVerificationLink = async (email) => {
        // Implement the logic to send verification link
        const object = {
            email: email
        }

        const result = await dispatch(sendVerificationEmail(object))
        if (result.data.code === 1) {
            Swal.fire(
                'Verification Link Sent',
                'Please check your email for the verification link.',
                'success'
            );
        }
        else {
            showWarningDialog(result.data.message)
        }

        // You might want to call an API endpoint here
    };

    const onFormSubmit = async (data) => {
        try {
            const res = await dispatch(authLogin(data));

            if (res.data.code === 1) {
                const user = {
                    access_token: res.data.access_token,
                    refresh_token: res.data.refresh_token,
                };
                login(user);
                //dispatch({ type: AUTH_LOGIN, payload: user });
                navigate("/dashboard");
            } else if (res.data.code === 3) {
                showEmailVerificationDialog(res.data.message, data);
            } else {
                showWarningDialog(res.data.message);
            }
        } catch (error) {
            showWarningDialog('An error occurred during login. Please try again.');
        }
    };

    const handleForgotPassword = (e) => {
        e.preventDefault(); // Prevent default navigation
        Swal.fire({
            title: 'Reset Password',
            input: 'email',
            inputLabel: 'Your email address',
            inputPlaceholder: 'Enter your email address',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Reset Password',
            cancelButtonText: 'Cancel',
            inputValidator: (value) => {
                if (!value) {
                    return 'You need to enter your email!';
                }
            }
        }).then(async (result) => {
            if (result.isConfirmed) {
                const email = result.value;
                try {
                    const resetPasswordResult = await dispatch(resetPassword({ email }));

                    if (resetPasswordResult.data.code === 1) {
                        Swal.fire(
                            'Password Reset Initiated',
                            'Check your email for further instructions.',
                            'success'
                        );
                    } else {
                        showWarningDialog(resetPasswordResult.data.message);
                    }
                } catch (error) {
                    showWarningDialog('An error occurred. Please try again.');
                }
            }
        });
    };

    return (
        <div style={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            background: '#f5f7fa',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, sans-serif',
        }}>
            <div style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '2rem',
                background: 'linear-gradient(120deg, #e3ecf7 0%, #f5f7fa 100%)',
                minHeight: '100vh',
                animation: 'fadeIn 1.2s cubic-bezier(0.4,0,0.2,1)'
            }}>
                <AppleCard style={{
                    width: '100%',
                    maxWidth: '1200px',
                    minHeight: '600px',
                    display: 'flex',
                    flexDirection: 'row',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                    borderRadius: '24px',
                    overflow: 'hidden',
                }}>
                    <div style={{
                        flex: 1,
                        background: 'linear-gradient(135deg, #e3ecf7 60%, #b6cbe0 100%)',
                        color: '#013e7d',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '2.5rem 1.5rem',
                        minWidth: 0,
                    }}>
                        <img src={whitelog} alt="Meinhardt Logo" style={{ maxWidth: '220px', marginBottom: '2.5rem' }} />
                        <h1 style={{ fontWeight: 600, fontSize: '2rem', marginBottom: '0.5rem', letterSpacing: '-1px', color: '#013e7d' }}>Welcome to M-SPHERE</h1>
                        <p style={{ fontSize: '1rem', fontWeight: 400, opacity: 0.92, marginBottom: '1.5rem', maxWidth: 320, textAlign: 'center', color: '#225080' }}>
                            Empowering Safety to the Frontline<br />Manage your HSSE operations efficiently and effectively with our comprehensive platform.
                        </p>
                    </div>
                    <div style={{
                        flex: 1.2,
                        background: 'rgba(255,255,255,0.98)',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        padding: '2.5rem 2rem',
                        minWidth: 0,
                    }}>
                        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                            <AppleCardHeader as="div" style={{
                                background: 'none',
                                color: '#013e7d',
                                padding: 0,
                                marginBottom: 0,
                                boxShadow: 'none',
                                justifyContent: 'center',
                            }}>
                                <h2 style={{ color: '#013e7d', fontWeight: 700, fontSize: '1.5rem', margin: 0 }}>M-SPHERE | HSE SYSTEM</h2>
                            </AppleCardHeader>
                            <div style={{ height: '3px', width: '32px', background: '#013e7d', margin: '0.5rem auto 0' }} />
                        </div>
                        <AppleCardBody style={{ padding: 0 }}>
                            <AppleForm onSubmit={handleSubmit(onFormSubmit)} autoComplete="off">
                                {error && <AppleAlert color="danger">{error}</AppleAlert>}
                                <AppleFormGroup style={{ position: 'relative' }}>
                                    <AppleLabel htmlFor="email">Email</AppleLabel>
                                    <div style={{ position: 'relative' }}>
                                        <FiMail style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#6c757d', fontSize: 18, zIndex: 2 }} />
                                        <Controller
                                            name="email"
                                            control={control}
                                            defaultValue=""
                                            rules={{
                                                required: "Email is required",
                                                pattern: {
                                                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                                    message: "Invalid email address"
                                                }
                                            }}
                                            render={({ field }) => (
                                                <AppleInput
                                                    {...field}
                                                    type="email"
                                                    placeholder="Enter your email"
                                                    style={{ paddingLeft: 44 }}
                                                    className={errors.email ? 'is-invalid' : ''}
                                                    autoFocus
                                                />
                                            )}
                                        />
                                    </div>
                                    {errors.email && <AppleFormFeedback>{errors.email.message}</AppleFormFeedback>}
                                </AppleFormGroup>
                                <AppleFormGroup style={{ position: 'relative' }}>
                                    <AppleLabel htmlFor="password">Password</AppleLabel>
                                    <div style={{ position: 'relative' }}>
                                        <FiLock style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#6c757d', fontSize: 18, zIndex: 2 }} />
                                        <Controller
                                            name="password"
                                            control={control}
                                            defaultValue=""
                                            rules={{
                                                required: "Password is required",
                                                minLength: {
                                                    value: 8,
                                                    message: "Password must be at least 8 characters long"
                                                }
                                            }}
                                            render={({ field }) => (
                                                <AppleInput
                                                    {...field}
                                                    type="password"
                                                    placeholder="Enter your password"
                                                    style={{ paddingLeft: 44 }}
                                                    className={errors.password ? 'is-invalid' : ''}
                                                />
                                            )}
                                        />
                                    </div>
                                    {errors.password && <AppleFormFeedback>{errors.password.message}</AppleFormFeedback>}
                                </AppleFormGroup>
                                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                                    <Link
                                        style={{ fontWeight: 500, color: '#013e7d', textDecoration: 'none', fontSize: '0.95rem' }}
                                        onClick={handleForgotPassword}
                                    >
                                        Forgot Password?
                                    </Link>
                                </div>
                                <AppleButton color="primary" block type="submit" style={{ width: '100%', marginBottom: 12, fontSize: '1.08rem', fontWeight: 600, background: '#013e7d', borderColor: '#013e7d' }}>
                                    Log In
                                </AppleButton>
                                <div style={{ textAlign: 'center', marginTop: 18, borderTop: '1px solid #f0f0f0', paddingTop: 18 }}>
                                    <span style={{ color: '#6c757d', fontSize: '0.98rem' }}>
                                        Don't have an account?{' '}
                                        <Link
                                            to="/register"
                                            style={{ fontWeight: 600, color: '#013e7d', textDecoration: 'none', position: 'relative' }}
                                            className="register-link"
                                        >
                                            Register
                                        </Link>
                                    </span>
                                </div>
                                {/* Support info below Register */}
                                <div style={{ textAlign: 'center', marginTop: 24 }}>
                                    <p style={{ margin: '0 0 0.5rem', fontSize: '0.93rem', color: '#013e7d', fontWeight: 500 }}>
                                        © {new Date().getFullYear()} HSSE. Crafted by M-SPHERE
                                    </p>
                                    <p style={{ color: '#013e7d', textDecoration: 'none', fontSize: '0.93rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                                        <MdEmail style={{ fontSize: '1rem' }} /> hsse.support@meinhardtepcm.com
                                    </p>
                                </div>
                            </AppleForm>
                        </AppleCardBody>
                    </div>
                </AppleCard>
            </div>
        </div>
    );
};

export default withRouter(Login);